/**
 * Addresses that come from the services, or from a company's job board
 * through them, before the page puts one in an `href`, a tab or the
 * window's location. React refuses a `javascript:` href, but not
 * `data:`, a custom scheme, or a script address given to
 * `location.assign` or to a tab opened on `about:blank`, which runs
 * with this page's origin. Every such address passes through here.
 */

const WEB_PROTOCOLS = new Set(["http:", "https:"]);

/** The address as a link a person can follow, or undefined when it is not a web page. */
export function webUrl(raw: string | null | undefined): string | undefined {
    if (!raw) return undefined;
    try {
        const url = new URL(raw.trim());
        return WEB_PROTOCOLS.has(url.protocol) && url.hostname ? url.href : undefined;
    } catch {
        return undefined;
    }
}

export interface ExternalUrlRules {
    /** The page's own origin: an address there is always allowed. */
    origin: string;
    /** When given, the only hosts an address elsewhere may be on. */
    hosts?: readonly string[];
}

/**
 * The address to send the browser to, or null when it is not one this
 * page sends anyone to: HTTPS (and on `hosts`, when they are named), or
 * this page's own origin.
 */
export function safeExternalUrl(
    raw: string | null | undefined,
    rules: ExternalUrlRules,
): string | null {
    if (!raw) return null;
    let url: URL;
    try {
        url = new URL(raw.trim(), rules.origin);
    } catch {
        return null;
    }
    if (url.origin === rules.origin && WEB_PROTOCOLS.has(url.protocol)) return url.href;
    if (url.protocol !== "https:" || !url.hostname) return null;
    if (rules.hosts && !rules.hosts.includes(url.hostname)) return null;
    return url.href;
}

/** Where billing sends a person: Stripe's checkout and its customer portal, and nowhere else. */
export const STRIPE_HOSTS = ["checkout.stripe.com", "billing.stripe.com"] as const;
